import { useMutation } from '@tanstack/react-query';
import { log } from '@hungryroot/o11y';
import { StatusCode } from '@hungryroot/rest';
import {
  signup as signupApiCall,
  type SignupParams,
  type SignupResponse,
} from '@hungryroot/rest/api/signup/post';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

type InvalidPasswordErrors = 'too-short' | 'too-common';

function getSignupError(resp: { status: number; data: unknown }) {
  const { data, status: respStatus } = resp;
  if (typeof data !== 'string') {
    return {
      type: 'unknown',
    } as const;
  }

  if (respStatus === 400) {
    switch (true) {
      case /password is too short/.test(data):
      case /password is too common/.test(data): {
        const errors: InvalidPasswordErrors[] = [];
        if (/password is too short/.test(data)) {
          errors.push('too-short');
        }

        if (/password is too common/.test(data)) {
          errors.push('too-common');
        }

        return {
          type: 'invalid-password',
          errors,
        } as const;
      }
      default: {
        return {
          type: 'bad-request',
        } as const;
      }
    }
  }

  if (respStatus === 409) {
    return {
      type: 'user-already-exists',
    } as const;
  }

  return {
    type: 'unknown',
  } as const;
}

type SignupError = ReturnType<typeof getSignupError>;

export function useSignup() {
  return useMutation<SignupResponse, SignupError, SignupParams>({
    mutationFn: async (params) => {
      ga('send', 'event', 'signup', 'submit');
      log('signup', {
        lifecycle: 'initiated',
      });

      const resp = await signupApiCall(params);

      const { status: respStatus } = resp;
      if (respStatus === StatusCode.SuccessOK) {
        const { data } = resp;
        return data;
      }

      throw getSignupError(resp);
    },
    onSuccess: (data) => {
      log('signup', {
        lifecycle: 'success',
      });
      ga('send', 'event', 'signup', 'success');
      window.dataLayer?.push({
        event: 'hr.signup.success',
        signup: {
          method: 'email',
        },
        customer: {
          id: data.id,
          email: data.email,
        },
      });
    },
  });
}
